import {isFeatureEnabled} from '@github-ui/feature-flags'
// eslint-disable-next-line no-restricted-imports -- This IS the sanitize facade; it wraps dompurify.
import DOMPurify, {type Config as DOMPurifyConfig} from 'dompurify'
import type {TrustedTypePolicy} from 'trusted-types/lib/index'

import type {SafeHTMLString} from './safe-html-string'
import {getSanitizePolicy, wrapAsTrustedHTML} from './trusted-types'
import type {Purifier} from './types'

/**
 * Sanitize HTML using DOMPurify with the browser's native window.
 *
 * This is the browser entry point — on the server, the "node" condition
 * in package.json exports resolves to sanitize.server.ts instead.
 *
 * String results are routed through the private `github-sanitize`
 * Trusted Types policy when the `trusted_types_sanitize_policy` feature
 * flag is on and `window.trustedTypes` is available, so that the output
 * can be safely assigned to TrustedHTML sinks in CSP-`trusted-types`-
 * enforced contexts.
 *
 * @param html - The HTML string to sanitize
 * @param config - Optional DOMPurify Config object.
 */
export function sanitize(dirty: string | Node, cfg: DOMPurifyConfig & {RETURN_DOM: true}): Node
export function sanitize(dirty: string | Node, cfg: DOMPurifyConfig & {RETURN_DOM_FRAGMENT: true}): DocumentFragment
export function sanitize(dirty: string | Node, cfg: DOMPurifyConfig & {RETURN_TRUSTED_TYPE: true}): TrustedHTML
export function sanitize(dirty: string | Node, cfg?: DOMPurifyConfig): SafeHTMLString
export function sanitize(
  dirty: string | Node,
  cfg?: DOMPurifyConfig,
): SafeHTMLString | Node | DocumentFragment | TrustedHTML {
  const _hardeningEnabled = isFeatureEnabled('sanitize_dompurify_hardening')
  // TODO: When hardening is implemented (#1724, #1725), this flag will gate
  // the DOMPurify hooks that strip js-* classes and block data-* attributes.
  const effectiveCfg = withSanitizePolicy(cfg)
  const result = DOMPurify.sanitize(dirty, effectiveCfg)
  // DOM / DocumentFragment / explicit Trusted Type return modes bypass the
  // string-wrapping path — DOMPurify already handled them.
  if (cfg && (cfg.RETURN_DOM || cfg.RETURN_DOM_FRAGMENT || cfg.RETURN_TRUSTED_TYPE)) {
    return result
  }
  return wrapAsTrustedHTML(result)
}

/**
 * Create a DOMPurify instance for advanced usage (e.g., adding hooks).
 * The returned purifier's `.sanitize()` is typed to return `SafeHTMLString`
 * for standard string sanitization calls.
 */
export function createPurifier(cb?: (purifier: Purifier) => void): Purifier {
  const purifier = DOMPurify() as unknown as Purifier
  if (cb) {
    cb(purifier)
  }
  return purifier
}

export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_DOM: true},
): Node
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_DOM_FRAGMENT: true},
): DocumentFragment
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_TRUSTED_TYPE: true},
): TrustedHTML
export function sanitizeWithPurifier(dirty: string | Node, purifier: Purifier, cfg?: DOMPurifyConfig): SafeHTMLString
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg?: DOMPurifyConfig,
): SafeHTMLString | Node | DocumentFragment | TrustedHTML {
  const effectiveCfg = withSanitizePolicy(cfg)
  const result = purifier.sanitize(dirty, effectiveCfg)
  // DOM / DocumentFragment / explicit Trusted Type return modes bypass the
  // string-wrapping path — DOMPurify already handled them.
  if (cfg && (cfg.RETURN_DOM || cfg.RETURN_DOM_FRAGMENT || cfg.RETURN_TRUSTED_TYPE)) {
    return result
  }
  return wrapAsTrustedHTML(result)
}

/**
 * When the caller requests `RETURN_TRUSTED_TYPE: true` and our
 * `github-sanitize` policy is available, inject it as DOMPurify's
 * `TRUSTED_TYPES_POLICY` so the returned TrustedHTML is minted by our
 * named policy (matching the one advertised in CSP) rather than
 * DOMPurify's default `dompurify` policy. Any caller-supplied policy
 * is overridden to enforce single-policy.
 */
function withSanitizePolicy<T extends DOMPurifyConfig | undefined>(cfg: T): T {
  if (!cfg || !cfg.RETURN_TRUSTED_TYPE) return cfg
  const policy = getSanitizePolicy()
  if (!policy) return cfg
  return {...cfg, TRUSTED_TYPES_POLICY: policy as TrustedTypePolicy}
}

// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {Config, Purifier, UponSanitizeAttributeHookEvent} from './types'
